import React, { type ComponentType, type ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import {
  ThemeProvider,
  ScopedCssBaseline,
  createTheme,
  Container,
  type ThemeOptions,
  type Theme,
  type ContainerProps,
} from '@mui/material';
import { ErrorBoundary } from '@components';
import { light } from '@styles';

const defaultTheme: Theme = createTheme(light as ThemeOptions);

export type InjectorOptions<P extends object = Record<string, never>> = {
  label?: string;
  onMissing?: (parent: HTMLElement) => void;
  theme?: Theme | ThemeOptions;
  baseline?: boolean;
  containerProps?: Partial<ContainerProps>;
  wrapper?: (node: ReactNode) => ReactNode;
  props?: P | undefined;
};

export function Injector<P extends object = Record<string, never>>(
  Component: ComponentType<P>,
  parent: HTMLElement,
  {
    label,
    onMissing,
    theme = defaultTheme,
    baseline = true,
    containerProps = { disableGutters: true, maxWidth: false },
    wrapper,
    props,
  }: InjectorOptions<P> = {},
): () => void {
  const isLogging = process.env?.NODE_ENV !== 'production' && label;

  if (isLogging) console.log(`${label}: Loading`);

  const render = () => {
    if (isLogging) console.log(`${label}: Initializing React component`);

    if (!parent) {
      if (isLogging) console.log(`${label}: No element to insert component into`);
      onMissing?.(parent);
      return () => {};
    }

    const root = ReactDOM.createRoot(parent);

    // Build the node (avoid JSX + generics issues)
    let node: ReactNode = React.createElement(Component, (props ?? {}) as P);

    // Compose the shell
    node = (
      <ErrorBoundary fallback={null}>
        <ThemeProvider theme={typeof theme === 'function' ? (theme as Theme) : (theme as Theme)}>
          {baseline ? (
            <ScopedCssBaseline>
              <Container {...containerProps}>{node}</Container>
            </ScopedCssBaseline>
          ) : (
            <Container {...containerProps}>{node}</Container>
          )}
        </ThemeProvider>
      </ErrorBoundary>
    );

    // Optional extra wrapper (e.g., per-page providers)
    if (wrapper) node = wrapper(node);

    root.render(node);
    return () => root.unmount();
  };

  if (document.readyState === 'loading') {
    if (isLogging) console.log(`${label}: Waiting for document ready state`);
    const handler = () => {
      render();
      document.removeEventListener('DOMContentLoaded', handler);
    };
    document.addEventListener('DOMContentLoaded', handler);
    return () => {};
  } else {
    return render();
  }
}
